import { type ComponentType, useLayoutEffect } from 'react';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { DoranProvider } from '@doranjs/react';
import { faIR } from '@doranjs/core';
import { Scrollbars } from 'react-custom-scrollbars';

import { darkTheme, lightTheme } from '../theme';
import { RTL } from '../components/mui-rtl';
import {
    HiddenTrack,
    ScrollView,
} from '../components/scrollbars/scroll-view';
import { themeModeSelector, useAppSelector } from './store';
import Router from './router/route';

// @doranjs/react 0.9.x is typed against the newer React JSX contract
// where function components may return ReactNode. This app is still on
// React 18 / TypeScript 4.8, so adapt only the provider's compile-time type.
// Runtime behavior remains the original DoranProvider.
const CompatibleDoranProvider = DoranProvider as unknown as ComponentType<
    Parameters<typeof DoranProvider>[0]
>;

function App() {
    const themeMode = useAppSelector(themeModeSelector);

    const theme = themeMode === 'dark' ? darkTheme : lightTheme;

    useLayoutEffect(() => {
        const background =
            themeMode === 'dark'
                ? darkTheme.palette.background.default
                : lightTheme.palette.background.default;

        document.documentElement.dataset.theme = themeMode;
        document.documentElement.dataset.doranTheme = themeMode;
        document.documentElement.style.colorScheme = themeMode;
        document.documentElement.style.backgroundColor = background;

        const themeColor = document.querySelector<HTMLMetaElement>(
            'meta[name="theme-color"]',
        );
        themeColor?.setAttribute('content', background);
    }, [themeMode]);

    return (
        <CompatibleDoranProvider locale={faIR} timeZone="Asia/Tehran">
            <RTL>
                <ThemeProvider theme={theme}>
                    <CssBaseline />

                    <Box
                        sx={{
                            width: '100%',
                            height: '100dvh',
                            overflow: 'hidden',
                        }}
                    >
                        <Scrollbars
                            renderView={ScrollView}
                            renderTrackHorizontal={HiddenTrack}
                            renderTrackVertical={HiddenTrack}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <Router />
                        </Scrollbars>
                    </Box>
                </ThemeProvider>
            </RTL>
        </CompatibleDoranProvider>
    );
}

export default App;
